import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Row, Col } from 'react-bootstrap';
import { Input, Fieldset, BackLink, Card, Details, Button, ErrorSummary } from 'nhsuk-react-components';
import { listFollowupCentres, createFollowupCentre, updateFollowupCentre, deleteFollowupCentre, getFollowupCentreDetails } from '../../actions/followupCentreActions';
import { FOLLOWUP_CENTRE_CREATE_RESET, FOLLOWUP_CENTRE_UPDATE_RESET } from '../../constants/followupCentreConstants';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import Callout from '../../components/callout/Callout.component';
import Modal from '../../components/modal/Modal.component';
import PageWrapper from '../../components/layout/PageWrapper.component';

const emptyCentre = {
    name: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    email: '',
    phoneNumber: '',
};

export default function FollowupCentreCRUDScreen({ history }) {
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = "Edit Followup Centres";
    }, []);

    useEffect(() => {
        dispatch({ type: FOLLOWUP_CENTRE_CREATE_RESET });
        dispatch({ type: FOLLOWUP_CENTRE_UPDATE_RESET });
    }, [dispatch]);

    const [followupCentreListFilter, setfollowupCentreListFilter] = useState({ query: '', page: 1, filter: 'Name' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newFollowupCentre, setNewFollowupCentre] = useState({ ...emptyCentre });
    const [updatedFollowupCentre, setUpdatedFollowupCentre] = useState({});

    const followupCentreList = useSelector((state) => state.followupCentreList);
    const { loading, error, followupCentres, currentPage, totalPages, totalCount } = followupCentreList;

    const followupCentreCreate = useSelector((state) => state.followupCentreCreate);
    const { success: successCreate, error: followupCentreCreateErrors, loading: followupCentreCreateLoading } = followupCentreCreate;

    const followupCentreUpdate = useSelector((state) => state.followupCentreUpdate);
    const { success: successUpdate, loading: followupCentreUpdateProcessing } = followupCentreUpdate;

    const followupCentreDetails = useSelector((state) => state.followupCentreDetails);
    const { followupCentre: followupCentreDet, loading: followupCentreDetailsLoading } = followupCentreDetails;

    useEffect(() => {
        dispatch(listFollowupCentres(followupCentreListFilter));
    }, [dispatch, followupCentreListFilter]);

    useEffect(() => {
        if (followupCentreDet) {
            setUpdatedFollowupCentre({ ...followupCentreDet });
        }
    }, [followupCentreDet]);

    useEffect(() => {
        if (successCreate) {
            setShowCreateModal(false);
            setNewFollowupCentre({ ...emptyCentre });
            dispatch(listFollowupCentres(followupCentreListFilter));
            dispatch({ type: FOLLOWUP_CENTRE_CREATE_RESET });
        }
    }, [successCreate, dispatch, followupCentreListFilter]);

    const handlePageChange = (newPage) => {
        setfollowupCentreListFilter({ ...followupCentreListFilter, page: newPage });
    };

    const handleSearchChange = (e) => {
        setfollowupCentreListFilter({ ...followupCentreListFilter, query: e.target.value, page: 1 });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this followupCentre?')) {
            dispatch(deleteFollowupCentre(id)).then(() => {
                dispatch(listFollowupCentres(followupCentreListFilter));
                if (updatedFollowupCentre?.id === id) {
                    setShowEditModal(false);
                    setUpdatedFollowupCentre({});
                }
            });
        }
    };

    const handleEditFollowupCentre = (id) => {
        dispatch(getFollowupCentreDetails(id));
        setShowCreateModal(false);
        setShowEditModal(true);
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        setUpdatedFollowupCentre({});
        dispatch({ type: FOLLOWUP_CENTRE_UPDATE_RESET });
    };

    const handleCreateFollowupCentre = () => {
        dispatch(createFollowupCentre(newFollowupCentre));
    };

    const handleUpdateFollowupCentre = () => {
        dispatch(updateFollowupCentre(updatedFollowupCentre.id, updatedFollowupCentre)).then(() => {
            dispatch(getFollowupCentreDetails(updatedFollowupCentre.id));
            dispatch(listFollowupCentres(followupCentreListFilter));
        });
    };

    const openCreateModal = () => {
        setNewFollowupCentre({ ...emptyCentre });
        dispatch({ type: FOLLOWUP_CENTRE_CREATE_RESET });
        setShowEditModal(false);
        setShowCreateModal(true);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col md="12">
                            <div className="welcome-box">
                                <PageWrapper title="FollowupCentre Management" totalCount={totalCount} loading={loading} />
                                <BackLink
                                    asElement="a"
                                    href="#"
                                    onClick={() => history.goBack()}
                                >
                                    Return back
                                </BackLink>
                                <Button
                                    className="nhsuk-button--small nhsuk-u-margin-bottom-4"
                                    onClick={openCreateModal}
                                >
                                    + Create FollowupCentre
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <div className="nhsuk-grid-column-one-half">
                        <Card>
                            <Card.Content>
                                <Card.Heading className="nhsuk-heading-m">
                                    <Input
                                        id="search-followupCentres"
                                        label="Search followupCentres"
                                        name="search-followupCentres"
                                        type="search"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearchChange(e);
                                            }
                                        }}
                                    />
                                </Card.Heading>
                                <Card.Description>
                                    <Fieldset>
                                        {followupCentres && followupCentres.length > 0 ? (
                                            followupCentres.map((followupCentre) => (
                                                <Details key={followupCentre.id || followupCentre._id}>
                                                    <Details.Summary>
                                                        {followupCentre.name}
                                                    </Details.Summary>
                                                    <Details.Text>
                                                        <p>
                                                            <strong>Address:</strong> {followupCentre.streetAddress}
                                                        </p>
                                                        <p>
                                                            <strong>City:</strong> {followupCentre.city}
                                                        </p>
                                                        <p>
                                                            <strong>State:</strong> {followupCentre.state}
                                                        </p>
                                                        <p>
                                                            <strong>Postal Code:</strong> {followupCentre.postalCode}
                                                        </p>
                                                        <p>
                                                            <strong>Country:</strong> {followupCentre.country}
                                                        </p>
                                                        <p>
                                                            <strong>Email:</strong> {followupCentre.email}
                                                        </p>
                                                        <p>
                                                            <strong>Phone Number:</strong> {followupCentre.phoneNumber}
                                                        </p>
                                                        <div className="compact-admin-table__actions">
                                                            <Button
                                                                className="nhsuk-button--small"
                                                                disabled={followupCentreDetailsLoading}
                                                                onClick={() => handleEditFollowupCentre(followupCentre.id)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                secondary
                                                                className="nhsuk-button--small btn-delete"
                                                                onClick={() => handleDelete(followupCentre.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Details.Text>
                                                </Details>
                                            ))
                                        ) : null}
                                    </Fieldset>
                                    {loading ? (
                                        <AppSpinner />
                                    ) : error ? (
                                        <p>{error}</p>
                                    ) : (
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            handlePageChange={handlePageChange}
                                        />
                                    )}
                                </Card.Description>
                            </Card.Content>
                        </Card>
                    </div>

                    <div className="nhsuk-grid-column-one-half" />
                </Container>
            </div>

            <Modal
                show={showCreateModal}
                handleClose={() => {
                    setShowCreateModal(false);
                    dispatch({ type: FOLLOWUP_CENTRE_CREATE_RESET });
                }}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Create FollowupCentre</h2>
                {followupCentreCreateErrors && (
                    <ErrorSummary aria-labelledby="error-summary-title" role="alert" tabIndex="-1">
                        <ErrorSummary.Title id="error-summary-title">There is a problem</ErrorSummary.Title>
                        <ErrorSummary.Body>
                            <ul className="nhsuk-list nhsuk-list--bullet">
                                {followupCentreCreateErrors.map((error) => (
                                    <li key={error}>{error}</li>
                                ))}
                            </ul>
                        </ErrorSummary.Body>
                    </ErrorSummary>
                )}
                <Input
                    id="name-create"
                    label="Name"
                    name="name-create"
                    type="text"
                    onChange={(e) => setNewFollowupCentre({ ...newFollowupCentre, name: e.target.value })}
                    value={newFollowupCentre?.name || ''}
                    required
                />
                <Input
                    id="streetAddress-create"
                    label="Street Address"
                    name="streetAddress-create"
                    type="text"
                    onChange={(e) => setNewFollowupCentre({ ...newFollowupCentre, streetAddress: e.target.value })}
                    value={newFollowupCentre?.streetAddress || ''}
                    required
                />
                <Input
                    id="city-create"
                    label="City"
                    name="city-create"
                    type="text"
                    onChange={(e) => setNewFollowupCentre({ ...newFollowupCentre, city: e.target.value })}
                    value={newFollowupCentre?.city || ''}
                    required
                />
                <Input
                    id="state-create"
                    label="State"
                    name="state-create"
                    type="text"
                    onChange={(e) => setNewFollowupCentre({ ...newFollowupCentre, state: e.target.value })}
                    value={newFollowupCentre?.state || ''}
                    required
                />
                <Input
                    id="postalCode-create"
                    label="Postal Code"
                    name="postalCode-create"
                    type="text"
                    onChange={(e) => setNewFollowupCentre({ ...newFollowupCentre, postalCode: e.target.value })}
                    value={newFollowupCentre?.postalCode || ''}
                    required
                />
                <Input
                    id="country-create"
                    label="Country"
                    name="country-create"
                    type="text"
                    onChange={(e) => setNewFollowupCentre({ ...newFollowupCentre, country: e.target.value })}
                    value={newFollowupCentre?.country || ''}
                    required
                />
                <Input
                    id="email-create"
                    label="Email"
                    name="email-create"
                    type="text"
                    onChange={(e) => setNewFollowupCentre({ ...newFollowupCentre, email: e.target.value })}
                    value={newFollowupCentre?.email || ''}
                    required
                />
                <Input
                    id="phoneNumber-create"
                    label="Phone Number"
                    name="phoneNumber-create"
                    type="text"
                    onChange={(e) => setNewFollowupCentre({ ...newFollowupCentre, phoneNumber: e.target.value })}
                    value={newFollowupCentre?.phoneNumber || ''}
                    required
                />
                <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                    <Button
                        className="nhsuk-button--small"
                        disabled={followupCentreCreateLoading || followupCentreUpdateProcessing}
                        onClick={handleCreateFollowupCentre}
                    >
                        Create FollowupCentre
                    </Button>
                    <Button
                        secondary
                        className="nhsuk-button--small"
                        onClick={() => {
                            setShowCreateModal(false);
                            dispatch({ type: FOLLOWUP_CENTRE_CREATE_RESET });
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </Modal>

            <Modal
                show={showEditModal}
                handleClose={handleCloseEdit}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Edit FollowupCentre</h2>
                {followupCentreDetailsLoading ? (
                    <AppSpinner />
                ) : (
                    <>
                        <Input
                            id="name"
                            label="Name"
                            name="name"
                            type="text"
                            onChange={(e) => setUpdatedFollowupCentre({ ...updatedFollowupCentre, name: e.target.value })}
                            value={updatedFollowupCentre.name ? updatedFollowupCentre.name : ''}
                            required
                        />
                        <Input
                            id="streetAddress"
                            label="Street Address"
                            name="streetAddress"
                            type="text"
                            onChange={(e) => setUpdatedFollowupCentre({ ...updatedFollowupCentre, streetAddress: e.target.value })}
                            value={updatedFollowupCentre.streetAddress ? updatedFollowupCentre.streetAddress : ''}
                            required
                        />
                        <Input
                            id="city"
                            label="City"
                            name="city"
                            type="text"
                            onChange={(e) => setUpdatedFollowupCentre({ ...updatedFollowupCentre, city: e.target.value })}
                            value={updatedFollowupCentre.city ? updatedFollowupCentre.city : ''}
                            required
                        />
                        <Input
                            id="state"
                            label="State"
                            name="state"
                            type="text"
                            onChange={(e) => setUpdatedFollowupCentre({ ...updatedFollowupCentre, state: e.target.value })}
                            value={updatedFollowupCentre.state ? updatedFollowupCentre.state : ''}
                            required
                        />
                        <Input
                            id="postalCode"
                            label="Postal Code"
                            name="postalCode"
                            type="text"
                            onChange={(e) => setUpdatedFollowupCentre({ ...updatedFollowupCentre, postalCode: e.target.value })}
                            value={updatedFollowupCentre.postalCode ? updatedFollowupCentre.postalCode : ''}
                            required
                        />
                        <Input
                            id="country"
                            label="Country"
                            name="country"
                            type="text"
                            onChange={(e) => setUpdatedFollowupCentre({ ...updatedFollowupCentre, country: e.target.value })}
                            value={updatedFollowupCentre.country ? updatedFollowupCentre.country : ''}
                            required
                        />
                        <Input
                            id="email"
                            label="Email"
                            name="email"
                            type="text"
                            onChange={(e) => setUpdatedFollowupCentre({ ...updatedFollowupCentre, email: e.target.value })}
                            value={updatedFollowupCentre.email ? updatedFollowupCentre.email : ''}
                            required
                        />
                        <Input
                            id="phoneNumber"
                            label="Phone Number"
                            name="phoneNumber"
                            type="text"
                            onChange={(e) => setUpdatedFollowupCentre({ ...updatedFollowupCentre, phoneNumber: e.target.value })}
                            value={updatedFollowupCentre.phoneNumber ? updatedFollowupCentre.phoneNumber : ''}
                            required
                        />
                        {successUpdate && (
                            <Callout
                                label="Success"
                                header="Success!"
                                autoDismiss={true}
                                dismissTime={5000}
                                time={new Date().toLocaleTimeString()}
                                title="Success!"
                                message="The followupCentre was updated successfully."
                                mode="success"
                            />
                        )}
                        <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                            <Button
                                className="nhsuk-button--small"
                                disabled={followupCentreUpdateProcessing}
                                onClick={handleUpdateFollowupCentre}
                            >
                                Save changes
                            </Button>
                            <Button
                                secondary
                                className="nhsuk-button--small"
                                onClick={handleCloseEdit}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </React.Fragment>
    );
}
